/**
 * Dashboard Service
 * Fetches KPIs, recent activity, and lists for the dashboard
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

// ========================================
// TYPES
// ========================================

/** The KPI definition this page renders (migration 1431). Any other definition is refused. */
export const DASHBOARD_KPI_DEFINITION = "dashboard-kpis/2";

export interface DashboardKPIs {
  members: {
    /** Memberships: distinct Zuora accounts with at least one live member contact. */
    accounts: number;
    /** Member contacts (primary + household) on those accounts. */
    people: number;
    /** Accounts whose earliest member start date is in the last 7 complete US Central days. */
    newAccounts7d: number;
    /** Member contacts whose start date is in the last 7 complete US Central days. */
    newPeople7d: number;
    /** Window of the "new" counts (YYYY-MM-DD, US Central, inclusive). */
    newFrom: string;
    newTo: string;
  };
  redemptions: {
    dollarValue: number;
    count: number;
  };
  supportTasks: {
    open: number;
    completed7d: number; // completed in the last 7 days (rolling)
  };
  upcomingEvents: number; // next 7 days, excluding deleted and cancelled events
}

export interface RecentActivityItem {
  id: string;
  type: "event" | "campaign" | "user";
  action: "created" | "updated" | "invited";
  title: string;
  timestamp: string;
  link?: string;
}

export interface UpcomingEvent {
  id: UUID;
  title: string;
  start_time: string;
  location_name?: string;
  /** "City, ST" when known */
  location_place?: string;
}

export interface ActiveCampaign {
  id: UUID;
  name: string;
  status: string;
  end_date?: string;
}

// ========================================
// KPI FUNCTIONS
// ========================================

function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Parse the get_dashboard_kpis() result. Throws unless it carries the expected definition, so
 * the page shows an error state instead of numbers computed under a different definition.
 */
export function parseDashboardKPIs(rpcResult: unknown): DashboardKPIs {
  const kpis =
    typeof rpcResult === "string" ? JSON.parse(rpcResult) : rpcResult;
  if (
    !kpis ||
    typeof kpis !== "object" ||
    (kpis as { definition?: unknown }).definition !== DASHBOARD_KPI_DEFINITION
  ) {
    throw new Error(
      `Dashboard numbers use an unexpected definition (expected ${DASHBOARD_KPI_DEFINITION})`,
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const k = kpis as any;
  return {
    members: {
      accounts: toNumber(k.members?.accounts),
      people: toNumber(k.members?.people),
      newAccounts7d: toNumber(k.members?.newAccounts7d),
      newPeople7d: toNumber(k.members?.newPeople7d),
      newFrom: String(k.members?.newFrom ?? ""),
      newTo: String(k.members?.newTo ?? ""),
    },
    redemptions: {
      dollarValue: toNumber(k.redemptions?.dollarValue),
      count: toNumber(k.redemptions?.count),
    },
    supportTasks: {
      open: toNumber(k.supportTasks?.open),
      completed7d: toNumber(k.supportTasks?.completed7d),
    },
    upcomingEvents: toNumber(k.upcomingEvents),
  };
}

/**
 * Get all dashboard KPIs via the single get_dashboard_kpis() RPC.
 *
 * There is deliberately NO fallback to direct table queries: the member count is distinct
 * Zuora accounts (not countable through PostgREST without reading every row) and the day /
 * week windows are US Central. A fallback would show different numbers under the same
 * labels, so on any RPC failure this throws and the page shows an error state.
 */
export async function getDashboardKPIs(): Promise<DashboardKPIs> {
  const { data, error } = await supabase.rpc("get_dashboard_kpis");
  if (error) {
    throw new Error(error.message || "get_dashboard_kpis failed");
  }
  return parseDashboardKPIs(data);
}

// ========================================
// RECENT ACTIVITY
// ========================================

/**
 * Get recent activity for a specific user
 * Tracks events, campaigns, and new users
 * Note: Currently shows all recent items as the tables don't have created_by tracking
 */
export async function getRecentActivity(
  _userId: string,
  limit: number = 10,
  includeCampaigns: boolean = true,
): Promise<RecentActivityItem[]> {
  const activities: RecentActivityItem[] = [];

  try {
    // Get recent events (show all recent events as there's no created_by tracking)
    // Use 'title' column which exists in production (not 'name')
    const { data: events, error: eventsError } = await supabase
      .from("events")
      .select("id, title, created_at, updated_at")
      .order("updated_at", { ascending: false })
      .limit(limit);

    if (!eventsError && events) {
      for (const event of events) {
        const isNew =
          new Date(event.created_at).getTime() ===
          new Date(event.updated_at).getTime();
        activities.push({
          id: event.id,
          type: "event",
          action: isNew ? "created" : "updated",
          title: event.title,
          timestamp: event.updated_at || event.created_at,
          link: `/events/${event.id}`,
        });
      }
    }

    // Get recent campaigns (if included)
    if (includeCampaigns) {
      const { data: campaigns, error: campaignsError } = await supabase
        .from("campaigns")
        .select("id, name, created_at, updated_at")
        .not("is_deleted", "is", true) // soft-deleted campaigns are hidden (migration 203)
        .order("updated_at", { ascending: false })
        .limit(limit);

      if (!campaignsError && campaigns) {
        for (const campaign of campaigns) {
          const isNew =
            new Date(campaign.created_at).getTime() ===
            new Date(campaign.updated_at).getTime();
          activities.push({
            id: campaign.id,
            type: "campaign",
            action: isNew ? "created" : "updated",
            title: campaign.name,
            timestamp: campaign.updated_at || campaign.created_at,
            link: `/campaigns/${campaign.id}`,
          });
        }
      }
    }

    // Get recently created users
    // Note: invited_by column doesn't exist in production, so just show recent users
    const { data: users, error: usersError } = await supabase
      .from("profiles")
      .select("id, full_name, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (!usersError && users) {
      for (const user of users) {
        activities.push({
          id: user.id,
          type: "user",
          action: "created",
          title: user.full_name || "New User",
          timestamp: user.created_at,
          link: "/users",
        });
      }
    }
  } catch (error) {
    console.error("Error fetching recent activity:", error);
  }

  // Sort by timestamp descending and limit
  return activities
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
    .slice(0, limit);
}

// ========================================
// LISTS
// ========================================

/** "City, ST" from whichever parts are present; undefined when neither is. */
export function formatEventPlace(
  city?: string | null,
  state?: string | null,
): string | undefined {
  const parts = [city?.trim(), state?.trim()].filter(Boolean);
  return parts.length ? parts.join(", ") : undefined;
}

/**
 * Get upcoming events (next 7 days), excluding deleted and cancelled events
 */
export async function getUpcomingEvents(
  limit: number = 5,
): Promise<UpcomingEvent[]> {
  const now = new Date();
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

  try {
    const { data, error } = await supabase
      .from("events")
      .select(
        "id, title, start_time, location_name, location_city, location_state, state",
      )
      .gte("start_time", now.toISOString())
      .lte("start_time", sevenDaysFromNow.toISOString())
      // Same rule as the Upcoming Events card (get_dashboard_kpis): not deleted, not cancelled.
      .not("is_deleted", "is", true)
      .is("deleted_at", null)
      .neq("status", "cancelled")
      .order("start_time", { ascending: true })
      .limit(limit);

    if (error) {
      console.error("Error fetching upcoming events:", error);
      return [];
    }

    return (data || []).map((event) => ({
      id: event.id,
      title: event.title,
      start_time: event.start_time,
      location_name: event.location_name || undefined,
      location_place: formatEventPlace(
        event.location_city,
        event.location_state || event.state,
      ),
    }));
  } catch (error) {
    console.error("Error fetching upcoming events:", error);
    return [];
  }
}

/**
 * Get active campaigns
 */
export async function getActiveCampaigns(
  limit: number = 5,
): Promise<ActiveCampaign[]> {
  const { data, error } = await supabase
    .from("campaigns")
    .select("id, name, status, end_date")
    .eq("status", "active")
    .not("is_deleted", "is", true) // soft-deleted campaigns are hidden (migration 203)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching active campaigns:", error);
    return [];
  }

  return data || [];
}
